import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Plus, Edit2, Trash2, MapPin, Eye, EyeOff, ShieldOff, Building2 } from "lucide-react";
import { locationsApi, type Location } from "@/lib/api/locations";
import { useLocations, LOCATIONS_QUERY_KEY } from "@/hooks/useLocations";
import { cn } from "@/lib/utils";

const LocationsSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: allLocations = [], isLoading: loading } = useLocations(true, {
    enabled: isTenantAdmin,
  });
  const locations = includeInactive ? allLocations : allLocations.filter((l) => l.isActive);
  const stats = {
    active: allLocations.filter((l) => l.isActive).length,
    inactive: allLocations.filter((l) => !l.isActive).length,
    total: allLocations.length,
  };

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleEdit = (location: Location) => {
    navigate(`/setup/locations/${location.id}/edit`);
  };

  const openDeleteDialog = (location: Location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!locationToDelete) return;

    try {
      setDeleting(true);
      const hardDelete = includeInactive || !locationToDelete.isActive;
      await locationsApi.delete(locationToDelete.id, hardDelete);

      const actionWord = hardDelete ? "permanently deleted" : "deactivated";
      toast.success(`"${locationToDelete.name}" has been ${actionWord}.`);
      queryClient.invalidateQueries(LOCATIONS_QUERY_KEY);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to delete location. Please try again.");
    } finally {
      setDeleting(false);
      setLocationToDelete(null);
    }
  };

  const handleNewClick = () => {
    navigate("/setup/locations/new");
  };

  // Filter locations by search query
  const filteredLocations = locations.filter(
    (location) =>
      location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="p-8 max-w-md text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <ShieldOff className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">You need administrator permissions to manage locations.</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Locations"
          description="Manage where library materials are stored and organized"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Locations" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name, code, or description..."
        />

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                <p className="text-sm text-gray-500">Active Locations</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-lg">
                <EyeOff className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
                <p className="text-sm text-gray-500">Inactive Locations</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-lg">
                <Building2 className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Locations</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Checkbox
              id="includeInactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            />
            <Label
              htmlFor="includeInactive"
              className="cursor-pointer text-gray-600 text-sm flex items-center gap-2"
            >
              {includeInactive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              Show inactive locations
            </Label>
          </div>
          <Button onClick={handleNewClick} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        </div>

        {/* Locations List */}
        <Card className="bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading locations..." />
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="py-16 text-center">
              <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <MapPin className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery ? "No matching locations" : "No locations yet"}
              </h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
                {searchQuery
                  ? "Try a different search term or check for typos."
                  : "Create your first location to organize where materials are stored in your library."}
              </p>
              {!searchQuery && (
                <Button onClick={handleNewClick} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Location
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredLocations.map((location) => (
                <div
                  key={location.id}
                  className={cn(
                    "p-5 hover:bg-gray-50 transition-colors",
                    !location.isActive && "bg-gray-50/50"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {location.name}
                        </h3>
                        {location.code && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                            {location.code}
                          </span>
                        )}
                        <span
                          className={cn(
                            "px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap",
                            location.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          )}
                        >
                          {location.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {location.description && (
                        <p className="text-sm text-gray-500 line-clamp-2">{location.description}</p>
                      )}
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(location)}
                        className="gap-1.5"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(location)}
                        className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {location.isActive ? "Deactivate" : "Delete"}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-white border border-gray-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-1">What are locations?</h4>
          <p className="text-sm text-gray-600">
            Locations represent physical areas in your library where materials are stored, such as
            "Main Reading Room," "Reference Section," or "Children's Corner." Assigning items to
            locations helps staff and patrons find materials quickly.
          </p>
        </div>
      </PageContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={
          locationToDelete?.isActive
            ? `Deactivate "${locationToDelete?.name}"?`
            : `Delete "${locationToDelete?.name}"?`
        }
        description={
          locationToDelete?.isActive ? (
            <>
              <p className="mb-2">
                Deactivating this location will hide it from selection lists. Items already assigned
                to this location will not be affected.
              </p>
              <p className="text-gray-600 font-medium">You can reactivate it later if needed.</p>
            </>
          ) : (
            <>
              <p className="mb-2">
                This will <strong>permanently delete</strong> this location. This action cannot be
                undone.
              </p>
              <p className="text-red-600 font-medium">
                Make sure no items are currently assigned to this location.
              </p>
            </>
          )
        }
        confirmLabel={locationToDelete?.isActive ? "Deactivate" : "Delete Permanently"}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="danger"
        loading={deleting}
      />
    </AppLayout>
  );
};

export default LocationsSettingsPage;
